import ActionButton from '@/components/Reusable/Button/ActionButton';
import {
  MFA_METHOD_EMAIL,
  MFA_METHOD_SMS,
  MFA_METHOD_TOTP,
} from '@/utils/constants/mfa';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Link, Radio, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import { useRouter } from 'next/router';

export default function MfaMethodSelect({
  hasEmail = true,
  hasPhone,
  hasTotp,
  maskedEmail,
  maskedPhone,
  selectedMethod,
  setSelectedMethod,
  onContinue,
  loading,
}) {
  const theme = useTheme();
  const router = useRouter();

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Icon + Heading + Description */}

      <Box sx={{ display: 'flex' }}>
        <Image
          src='/images/ic-password.svg'
          alt='Password Icon'
          width={60}
          height={60}
          priority
        />
        <Box sx={{ ml: 2, mb: 3 }}>
          <Typography
            component='h1'
            variant='h1'
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Multi-factor authentication (MFA)
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.4,
            }}
          >
            {hasPhone || hasTotp
              ? 'You have been enrolled in multi-factor authentication. Select the method you would like to use to receive an authentication code.'
              : 'You have been enrolled in multi-factor authentication. The email in your user profile will be used to send an authentication code.'}
          </Typography>
        </Box>
      </Box>

      {/* "Which method would you like to use?" Heading */}
      <Typography
        variant='subtitle1'
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
        }}
      >
        Which method would you like to use?
      </Typography>

      {/* Option: Email */}
      {hasEmail && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              id='method-email'
              name='mfaMethod'
              checked={selectedMethod === MFA_METHOD_EMAIL}
              onChange={() => setSelectedMethod(MFA_METHOD_EMAIL)}
              color='primary'
              sx={{ mr: 1 }}
              inputProps={{ 'aria-label': 'Email' }}
            />
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Email
            </Typography>
          </Box>
          <Typography
            variant='body1'
            sx={{
              color: theme.palette.text.secondary,
              ml: 6, // Adjust this to match the combined width of the radio + label
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              maxWidth: '100%',
            }}
          >
            {maskedEmail}
          </Typography>
        </Box>
      )}

      {/* Option: TOTP (if available) */}
      {/* TOTP option only shown if user has TOTP enabled */}
      {hasTotp && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Radio
              id='method-totp'
              name='mfaMethod'
              checked={selectedMethod === MFA_METHOD_TOTP}
              onChange={() => setSelectedMethod(MFA_METHOD_TOTP)}
              color='primary'
              sx={{ mr: 1 }}
              inputProps={{ 'aria-label': 'Authenticator App (TOTP)' }}
            />
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Authenticator App (TOTP)
            </Typography>
          </Box>
        </Box>
      )}

      {/* Option: Phone (if available) */}
      {/* SMS option only shown if user has verified phone number */}
      {hasPhone && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Radio
              id='method-phone'
              name='mfaMethod'
              checked={selectedMethod === MFA_METHOD_SMS}
              onChange={() => setSelectedMethod(MFA_METHOD_SMS)}
              color='primary'
              sx={{ mr: 1 }}
              inputProps={{ 'aria-label': 'SMS (text message)' }}
            />
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.text.secondary,
                mr: 1,
              }}
            >
              SMS (text message)
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.text.secondary,
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                maxWidth: '100%',
              }}
            >
              {maskedPhone}
            </Typography>
          </Box>
          {/* SMS opt-in verbiage – shown when SMS is an option */}
          <Typography
            variant='caption'
            component='div'
            sx={{
              color: theme.palette.text.secondary,
              ml: 6,
              textAlign: 'left',
              lineHeight: 1.5,
            }}
          >
            By selecting SMS (text message) you agree to receive OTP messages
            via text message from Cornerstone Information Systems. If you choose
            to receive OTP codes by text, we will send one text per login
            request. Message and data rates may apply. To stop receiving these
            messages, choose Email as your preferred method in your user profile
            or reply &quot;STOP&quot; to a received message. Reply
            &quot;HELP&quot; for help. For customer care, call 1-888-778-0897 or
            email support@ciswired.com. <br />
            <Link
              href='/terms-of-use'
              target='_blank'
              rel='noopener noreferrer'
              sx={{ mr: 0.5 }}
            >
              Terms and Conditions
            </Link>
            |
            <Link
              href='/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              sx={{ ml: 0.5 }}
            >
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      )}

      {/* Continue Button */}
      <ActionButton
        loading={loading}
        disabled={loading || !selectedMethod}
        onClick={onContinue}
        sx={{ mb: 2 }}
      >
        Continue
      </ActionButton>

      {/* "← Return to sign in" Button */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant='text'
          onClick={() => router.reload()}
          startIcon={<ChevronLeftIcon />}
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'none',
          }}
        >
          Return to sign in
        </Button>
      </Box>
    </Box>
  );
}
